import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { TextField, Box, CircularProgress } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import type { Company } from '../../services/rtkApi';

import { handleOrgnummerValidering } from '../../utils';
import { useCompanySearch } from './useCompanySearch';
import useDebounce from '@forvalter/utils/useDebounce';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { getFormFieldStyles } from '@klp/formgenerator/formFieldStyles';
import { useFormContext } from '@klp/formgenerator';

type CompanySearchfieldProps = {
    id: string;
    label: string;
    placeholder?: string;
    selectedCompany: Company | null;
    error?: string;
    isReadOnly: boolean;
    isRequired?: boolean;
    defaultValue?: string;
    onChange: (company: Company | null) => void;
    onClear: () => void;
    // @deprecated. Use company context to read selected company instead of callback
    onSelected?: (company: Company | null) => void;
};

export const CompanySearchField = ({
    id,
    label,
    placeholder,
    error: errorProp,
    isReadOnly,
    isRequired,
    selectedCompany,
    onChange,
    onClear,
    onSelected,
}: CompanySearchfieldProps) => {
    const { t } = useTranslation();
    const classes = getFormFieldStyles();
    const [inputValue, setInputValue] = useState('');
    const [componentValidationError, setComponentValidationError] =
        useState<string>();
    const { debouncedValue: debouncedInputValue, isDebouncing } = useDebounce(
        inputValue,
        1500,
    );

    const { regionContext } = useFormContext();
    const { companyOptions, companiesIsLoading, searchForCompanies } =
        useCompanySearch();

    // Handle company selection and update state accordingly
    const handleCompanyChanged = useCallback(
        (company: Company | null) => {
            if (onSelected) {
                onSelected(company); // @deprecated callback, we set company in context instead
            }
            onChange(company);
        },
        [onSelected, onChange],
    );

    // Debounced search for companies when user changes input value
    useEffect(() => {
        if (inputValue.length <= 2 || selectedCompany) return;

        const formattedInput = inputValue.replace(/[\s-]+/g, '');
        const isNumber = /^\d+$/.test(formattedInput);

        if (isNumber || regionContext === 'Sverige') {
            if (handleOrgnummerValidering(formattedInput, regionContext)) {
                searchForCompanies(formattedInput);
            } else {
                setComponentValidationError(
                    t(`project.errors.invalidOrgnumber`),
                );
            }

            return;
        }

        searchForCompanies(debouncedInputValue);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedInputValue]);

    // Memoized computation of displayed company name - avoid re-renders
    const getDisplayedCompanyName = useMemo(() => {
        setComponentValidationError(undefined);
        if (selectedCompany) {
            return `${selectedCompany.navn} (${selectedCompany.organisasjonsnummer})`;
        }

        return inputValue;
    }, [selectedCompany, inputValue]);

    // If field is read-only and a company is selected, do not render the search field
    // as we'll show only summary and no edit field
    if (isReadOnly && selectedCompany) return null;

    const error = errorProp || componentValidationError;
    const isLoading = companiesIsLoading || isDebouncing;

    return (
        <Box width="100%" data-testid="company-search-field">
            <Autocomplete<Company>
                id={id}
                fullWidth={true}
                selectOnFocus={true}
                clearOnBlur={true}
                handleHomeEndKeys={true}
                disabled={isReadOnly}
                value={selectedCompany}
                onInputChange={(_event, newInputValue, reason) => {
                    if (reason === 'clear') {
                        setInputValue('');
                        onClear();
                        return;
                    }

                    if (reason === 'input') {
                        setInputValue(newInputValue);
                    }
                }}
                onChange={(_event, newValue, reason) => {
                    if (reason === 'clear') {
                        onClear();
                        return;
                    }
                    if (Array.isArray(newValue)) {
                        handleCompanyChanged(newValue[0]);
                    } else {
                        handleCompanyChanged(newValue);
                    }
                }}
                getOptionSelected={(option, value) =>
                    option.organisasjonsnummer === value.organisasjonsnummer
                }
                filterOptions={(options, _state) => options}
                getOptionLabel={(option) =>
                    `${option.navn} (${option.organisasjonsnummer})`
                }
                options={companyOptions}
                loading={isLoading}
                inputValue={getDisplayedCompanyName}
                renderInput={(params) => (
                    <TextField
                        required={isRequired}
                        className={classNames({
                            [classes.editableField]: !isReadOnly,
                            [classes.readOnlyField]: isReadOnly,
                        })}
                        {...params}
                        label={label}
                        placeholder={placeholder}
                        variant="outlined"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {isLoading && (
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                        />
                                    )}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                        inputProps={{
                            ...params.inputProps,
                            'data-testid': 'company-search-input',
                        }}
                        error={Boolean(error)}
                        helperText={error ? error : ''}
                    />
                )}
            />
        </Box>
    );
};
